import React, { useEffect, useState, useRef } from 'react';
import * as yup from "yup";
import CustomTable from '../../components/common/Table.tsx';
import Searchbar from '../../components/common/Searchbar.tsx';
import SideSheet from '../../components/common/Sidesheet.tsx';
import { Formik, FormikProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store.ts';
import RequiredLabel from '../../components/common/RequiredLabel.tsx';
import Confirmation from '../../components/common/Confirmation.tsx';
import { notification, Pagination } from 'antd';
import MapComponent from '../../components/common/map/GoogleMap.tsx';
import { useNavigate, useLocation } from 'react-router-dom';
import RequestForm from '../../components/sheet/RequestForm.tsx';
import { capitalizeFirstLetter } from '../../utils/util.service.ts';
import { setBreadcrumbs } from '../../slices/BreadcrumbSlice.ts';
import { getAllRequest, addRequest, updateRequest, deleteRequest, setOffset, setLimit, setSortField, setSortOrder, setSearchInput, getRequestById } from '../../slices/RequestSlice.tsx';
import { IRequestReq, IRequestResponse, IRequestRes, IRequest, ISubService, ICommonAPIResponse } from '../../type/type.ts';
import { PayloadAction } from '@reduxjs/toolkit';

interface RequestFormValues {
  id: number;
  customer_id: number;
  service_id: number;
  sub_service_id: number;
  customer_name: string;
  service_name: string;
  sub_service_name: string;
  created_at: string;
}

interface IRequestByIdData {
  id: number;
  created_at: string;
  updated_at: string;
  created_by: null;
  updated_by: null;
  deletedAt: null;
  deletedBy: null;
  customer_id: number;
  service_id: number;
  sub_service_id: number;
  customer: {
    id: number;
    name: string;
    email: string;
    mobile: string;
    age: number;
    status: string;
  };
  service: {
    id: number;
    name: string;
  };
  subService: {
    id: number;
    name: string;
    service_id: number;
  };
}

interface IRequestByIdResponse extends ICommonAPIResponse {
  data: IRequestByIdData;
}

const Requests: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [isSideSheetOpen, setSideSheetOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<number | null>(null);
  const { requests, loading, dataCount, offset, limit, } = useSelector((state: RootState) => state.requestSlice);
  const [sortedData, setSortedData] = useState<RequestFormValues[]>([]);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmationMessage, setConfirmationMessage] = useState("Do you want to submit the form?");
  // const { getSitebyIdvalue } = useSelector((state: RootState) => state.siteManagement);

  const navigate = useNavigate();
  const [selectedRequest, setSelectedRequest] = useState<IRequestRes | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    order: "ascend" | "descend" | null;
  }>({
    key: "",
    order: null,
  });

  const initialValues: IRequestReq = {
    customer_id: 0,
    service_id: 0,
    sub_service_id: 0,
    customer_name: "",
    service_name: "",
    sub_service_name: "",
    created_at: new Date().toISOString()
  };

  const showNotification = (type: "success" | "error", message: string) => {
    notification[type]({
      message,
      placement: "top",
    });
  };
  const handleSearch = (value: string) => {
    setQuery(value);
    dispatch(setOffset(0));
  };

  const handlePaginationChange = (page: number, newPageSize?: number) => {
    dispatch(setOffset(page - 1));
    if (newPageSize && newPageSize !== limit) {
      dispatch(setLimit(newPageSize));
    }
  };

  const handleSortChange = (_: any, __: any, sorter: any) => {
    if (Array.isArray(sorter)) {
      sorter = sorter[0];
    }

    const { field, order } = sorter || {};

    const sortOrder = order === 'ascend' ? 1 : -1;

    setSortConfig({
      key: field || null,
      order: order || null
    });

    dispatch(setSortField(field));
    dispatch(setSortOrder(sortOrder));

    dispatch(setOffset(offset));
  };

  useEffect(() => {
    dispatch(getAllRequest({
      offset: offset,
      limit: limit,
      sortField: sortConfig.key ?? null,
      sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null,
      searchInput: query
    }));
  }, [dispatch, offset, limit, sortConfig, query]);

  useEffect(() => {
    if (selectedRequest) {
      dispatch(getAllRequest({
        offset: 0,
        limit: 10,
        sortField: "",
        sortOrder: 1,
        searchInput: ""
      }));
    }
  }, [dispatch, selectedRequest]);

  // useEffect(() => {
  //   if (location.state?.editMode && location.state?.siteData) {
  //     const siteData = location.state.siteData as IRequestRes;
  //     setSelectedRequest(siteData);
  //     setSideSheetOpen(true);

  //     setTimeout(() => {
  //       if (formikRef.current) {
  //         formikRef.current.setValues({
  //           customer_id: siteData.customer_id || 0,
  //           service_id: siteData.service_id || 0,
  //           sub_service_id: siteData.sub_service_id || 0,
  //           customer_name: siteData.name || '',
  //           service_name: siteData.service_name || '',
  //           sub_service_name: siteData.sub_service_name || '',
  //           created_at: siteData.created_at || new Date().toISOString()
  //         });
  //       }
  //     }, 0);
  //   }
  // }, [location.state]);

  useEffect(() => {
    dispatch(setBreadcrumbs([
      {
        title: "Requests",
        path: '/requests'
      }
    ]));
  }, []);

  const handleEditClick = (request: IRequestRes) => {
    try {
      dispatch(getRequestById(request.id)).then((action) => {
        // Wait for the request data to be loaded
        const response = action.payload as IRequestByIdResponse;
        if (response?.data) {
          const requestData = response.data;
          setSelectedRequest(request);
          setSideSheetOpen(true);
          
          // Set form values when editing with proper type checking
          if (formikRef.current) {
            const formValues: IRequestReq = {
              customer_id: requestData.customer_id,
              service_id: requestData.service_id,
              sub_service_id: requestData.sub_service_id,
              customer_name: requestData.customer?.name || '',
              service_name: requestData.service?.name || '',
              sub_service_name: requestData.subService?.name || '',
              created_at: requestData.created_at
            };
            
            // Set the form values
            formikRef.current.setValues(formValues);
            
            // Trigger validation to update touched state
            formikRef.current.validateForm();
            
            // Set touched state for the fields to show validation
            formikRef.current.setTouched({
              customer_id: true,
              service_id: true,
              sub_service_id: true,
              customer_name: true,
              service_name: true,
              sub_service_name: true
            });
          }
        }
      });
    } catch (error) {
      showNotification("error", "Failed to load request data for editing");
      console.error("Error in handleEditClick:", error);
    }
  };

  const handleDeleteClick = (request: IRequestRes) => {
    setRequestToDelete(request.id);
    setConfirmationMessage("Are you sure you want to delete this request?");
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (requestToDelete) {
      try {
        await dispatch(deleteRequest(requestToDelete)).unwrap();
        showNotification("success", "Request deleted successfully");
        await dispatch(getAllRequest({
          offset: offset,
          limit: limit,
          sortField: sortConfig.key ?? null,
          sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null,
          searchInput: query
        }));
      } catch (error) {
        showNotification("error", error.message || "Failed to delete request");
      }
      setIsDeleteConfirmationOpen(false);
      setRequestToDelete(null);
    }
  };

  const columns = [
    {
      title: <span className="font-montserrat font-medium text-[13px] leading-5 tracking-normal text-[#6D6D6D] capitalize">S.No</span>,
      dataIndex: "id",
      key: "serialNumber",
      render: (_: any, __: any, index: number) =>
        (offset * limit + index + 1).toString().padStart(2, "0"),
      width: 50,
    },
    {
      title: <span className="text-[#6D6D6D]">Client Name</span>,
      dataIndex: 'name',
      key: 'clientName',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "name" ? sortConfig.order : null,
      render: (name: string) => <span className="text-[#0A0A0A] font-medium text-sm">{capitalizeFirstLetter(name)}</span>,
    },
    {
      title: <span className="text-[#6D6D6D]">Care Category</span>,
      dataIndex: 'service_name',
      key: 'serviceName',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "service_name" ? sortConfig.order : null,
      render: (serviceName: string) => <span className="text-[#0A0A0A] font-medium text-sm">{capitalizeFirstLetter(serviceName)}</span>,
    },
    {
      title: <span className="text-[#6D6D6D]">Care Type</span>,
      dataIndex: 'sub_service_name',
      key: 'subServiceName',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "sub_service_name" ? sortConfig.order : null,
      render: (subServiceName: string) => <span className="text-[#0A0A0A] font-medium text-sm">{capitalizeFirstLetter(subServiceName)}</span>,
    },
    {
      title: <span className="text-[#6D6D6D]">Created At</span>,
      dataIndex: 'created_at',
      key: 'createdAt',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "created_at" ? sortConfig.order : null,
      render: (createdAt: string) => <span className="text-[#0A0A0A] font-medium text-sm">{new Date(createdAt).toLocaleDateString()}</span>,
    },
    {
      title: <span className="text-[#6D6D6D]">Action</span>,
      dataIndex: 'action',
      key: 'action',
      sorter: false,
      render: (_: any, record: IRequestRes) => (
        <div className="flex items-center gap-3">
          <img
            src="/client/edit.svg"
            alt="Edit"
            className="w-5 h-5 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => handleEditClick(record)}
          />
          <img
            src="/client/delete.svg"
            alt="Delete"
            className="w-5 h-5 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => handleDeleteClick(record)}
          />
        </div>
      )
    }
  ];

  const requestSchema = yup.object().shape({
    customer_id: yup.number().required("Client is required"),
    service_id: yup.number().required("Care Category is required"),
    sub_service_id: yup.number().required("Care Type is required"),
    customer_name: yup.string().required("Client Name is required"),
    service_name: yup.string().required("Care Category is required"),
    sub_service_name: yup.string().required("Care Type is required"),
  });

  const formikRef = useRef<FormikProps<IRequestReq>>(null);
  
  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={requestSchema}
      enableReinitialize={true}
      onSubmit={async (values, { resetForm }) => {
        setConfirmationMessage(selectedRequest ? "Do you want to update the request?" : "Do you want to submit the form?");
        setIsConfirmationOpen(true);
        setConfirmAction(() => async () => {
          try {
            const requestData: IRequestReq = {
              customer_id: values.customer_id,
              service_id: values.service_id,
              sub_service_id: values.sub_service_id,
              customer_name: values.customer_name,
              service_name: values.service_name,
              sub_service_name: values.sub_service_name,
              created_at: values.created_at
            };
            
            if (selectedRequest) {
              // Validate required fields before update
              if (!requestData.customer_id || !requestData.service_id || !requestData.sub_service_id) {
                showNotification("error", "Please fill in all required fields");
                return;
              }

              // Update existing request with proper error handling
              try {
                const updatePayload = {
                  id: selectedRequest.id,
                  customer_id: values.customer_id,
                  service_id: values.service_id,
                  sub_service_id: values.sub_service_id
                };
                
                await dispatch(updateRequest(updatePayload)).unwrap();
                showNotification("success", "Request updated successfully");
                
                // Refresh the table data
                await dispatch(getAllRequest({
                  offset: offset,
                  limit: limit,
                  sortField: sortConfig.key ?? null,
                  sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null,
                  searchInput: query
                }));
                
                // Reset form and close side sheet
                resetForm();
                setSideSheetOpen(false);
                setSelectedRequest(null);
              } catch (error) {
                const errorMessage = error?.message || "Failed to update request";
                showNotification("error", errorMessage);
                console.error("Error updating request:", error);
              }
            } else {
              // Create new request
              await dispatch(addRequest(requestData)).unwrap();
              setSideSheetOpen(false);
              showNotification("success", "Request created successfully");
              
              // Refresh the table data
              await dispatch(getAllRequest({
                offset: offset,
                limit: limit,
                sortField: sortConfig.key ?? null,
                sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null,
                searchInput: query
              }));
            }
          } catch (error) {
            showNotification("error", "An unexpected error occurred");
            console.error("Unexpected error in form submission:", error);
          }
          setIsConfirmationOpen(false);
        });
      }}
    >
      {({ handleSubmit, values, touched, errors, setFieldValue, handleChange, resetForm, setFieldTouched, validateField }) => (
        <>
          <div className="bg-[#EDF1F6] h-[76px] mt-[10px] flex justify-between items-center px-[20px]">
            <p className="font-Montserrat font-semibold text-[20px] leading-[24.38px] text-[#0A0A0A] ">Requests</p>
            <div className="flex justify-end gap-3 ">
              <div className="flex justify-end ">
                <Searchbar search={query} onSearch={handleSearch} />
              </div>

              <SideSheet
                btnLabel={selectedRequest ? "Edit Request" : "Add New Request"}
                width={800}
                title={selectedRequest ? "Edit Request" : "Add New Request"}
                isOpen={isSideSheetOpen}
                setOpen={setSideSheetOpen}
                onCancel={() => {
                  setSideSheetOpen(false);
                  resetForm();
                  setSelectedRequest(null);
                }}
                onSubmit={handleSubmit}
                submitLabel={selectedRequest ? "Update" : "Submit"}
              >
                <RequestForm
                  handleChange={handleChange}
                  values={values}
                  touched={touched} 
                  errors={errors}
                  setFieldValue={setFieldValue}
                />
              </SideSheet>
            </div>
          </div>
          <div>
            <CustomTable
              columns={columns}
              dataSource={requests}
              loading={loading}
              rowKey="id"
              onChange={handleSortChange}
              scrollHeight={800}
            />
          </div>
          <div className=" sticky bottom-0 flex justify-end py-4">
            <Pagination
              total={dataCount}
              showSizeChanger={false}
              onChange={handlePaginationChange}
            />
          </div>
          <Confirmation
            label="Confirmation"
            message="Do you want to submit the form?"
            isOpen={isConfirmationOpen}
            onClose={() => setIsConfirmationOpen(false)}
            onConfirm={confirmAction || (() => { })}
            confirmButtonLabel="Confirm"
          />
          <Confirmation
            label="Delete Confirmation"
            message={confirmationMessage}
            isOpen={isDeleteConfirmationOpen}
            onClose={() => {
              setIsDeleteConfirmationOpen(false);
              setRequestToDelete(null);
            }}
            onConfirm={handleDeleteConfirm}
            confirmButtonLabel="Delete"
          />
        </>
      )}
    </Formik>
  );
};
export default Requests;